import { normalizeTips, type Tip } from './tip.js';

export interface CommandResult<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  tips: Tip[];
  meta?: {
    duration?: number;
    command?: string;
    site?: string;
  };
}

/**
 * Build a success CommandResult.
 * Backward compatible: accepts both `string[]` (legacy plugins) and `Tip[]` (new).
 * The returned `tips` is always normalized to `Tip[]`.
 */
export function ok<T>(data: T, tips?: Array<string | Tip>): CommandResult<T> {
  return { success: true, data, tips: normalizeTips(tips) };
}

/**
 * Build a failure CommandResult.
 * Backward compatible: accepts both `string[]` (legacy plugins) and `Tip[]` (new).
 * The returned `tips` is always normalized to `Tip[]`.
 */
export function fail(message: string, tips?: Array<string | Tip>): CommandResult<null> {
  return { success: false, data: null, message, tips: normalizeTips(tips) };
}

export function withMeta<T>(
  result: CommandResult<T>,
  meta: CommandResult<T>['meta']
): CommandResult<T> {
  return { ...result, meta: { ...result.meta, ...meta } };
}

export function isCommandResult(value: unknown): value is CommandResult {
  return (
    value !== null &&
    typeof value === 'object' &&
    'success' in (value as Record<string, unknown>) &&
    'tips' in (value as Record<string, unknown>)
  );
}

export function wrapResult<T>(raw: T): CommandResult<T> {
  if (isCommandResult(raw)) {
    return raw as CommandResult<T>;
  }
  return ok(raw);
}
